import type { FrameworkVersionSummary } from '../../types';
import { X, Clock, User } from 'lucide-react';
import { Button } from '../common/Button';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { useFrameworkVersions } from '../../hooks/useFramework';

interface FrameworkVersionHistoryProps {
  onSelectVersion: (version: string) => void;
  onClose: () => void;
}

export function FrameworkVersionHistory({ onSelectVersion, onClose }: FrameworkVersionHistoryProps) {
  const { data: versionsData, isLoading, error } = useFrameworkVersions();

  const versions = versionsData || [];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-wolf-800 rounded-lg w-full max-w-2xl max-h-[80vh] flex flex-col border border-wolf-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-wolf-700">
          <h2 className="text-xl font-semibold text-white">Version History</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-wolf-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading && (
            <div className="flex justify-center py-8">
              <LoadingSpinner message="Loading versions..." />
            </div>
          )}

          {error && (
            <p className="text-red-400 text-center py-8">Failed to load version history</p>
          )}

          {!isLoading && !error && versions.length === 0 && (
            <p className="text-wolf-400 text-center py-8">No versions found</p>
          )}

          {!isLoading && !error && versions.length > 0 && (
            <div className="space-y-3">
              {versions.map((v: FrameworkVersionSummary, index: number) => (
                <div
                  key={v.version}
                  className={`bg-wolf-900 rounded-lg p-4 border ${
                    index === 0 ? 'border-action' : 'border-wolf-700'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-white">{v.version}</span>
                        {index === 0 && (
                          <span className="text-xs bg-action/20 text-action px-2 py-0.5 rounded">
                            Active
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-wolf-300 mt-1">{v.name}</p>
                      {v.version_notes && (
                        <p className="text-sm text-wolf-400 mt-2 italic">{v.version_notes}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-xs text-wolf-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(v.created_at).toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {v.created_by?.name || 'Unknown'}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onSelectVersion(v.version)}
                    >
                      View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-wolf-700">
          <Button variant="secondary" onClick={onClose} className="w-full">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
}
